import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  DollarSign, 
  TrendingUp,
  TrendingDown,
  PiggyBank,
  AlertCircle,
  ArrowUpRight,
  ArrowDownRight,
  Calendar
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FinancePage = () => {
  const { token, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [incomes, setIncomes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [periodo, setPeriodo] = useState('mes');

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/finance/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [token]);

  const fetchIncomes = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/finance/incomes?periodo=${periodo}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIncomes(response.data);
    } catch (error) {
      console.error('Error fetching incomes:', error);
    } finally {
      setLoading(false);
    }
  }, [token, periodo]);

  useEffect(() => {
    fetchStats();
    fetchIncomes();
  }, [fetchStats, fetchIncomes]);

  const formatCurrency = (amount) => {
    return `Q ${amount?.toLocaleString('es-GT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}`;
  };

  const getCategoryBadge = (cat) => {
    const styles = {
      CONSULTAS: 'bg-blue-100 text-blue-700',
      CIRUGIAS: 'bg-purple-100 text-purple-700',
      MEDICAMENTOS: 'bg-green-100 text-green-700',
      PROCEDIMIENTOS: 'bg-teal-100 text-teal-700',
      OTROS: 'bg-gray-100 text-gray-600'
    };
    const labels = {
      CONSULTAS: 'Consultas',
      CIRUGIAS: 'Cirugías',
      MEDICAMENTOS: 'Medicamentos',
      PROCEDIMIENTOS: 'Procedimientos',
      OTROS: 'Otros'
    };
    return <Badge className={styles[cat] || 'bg-gray-100'}>{labels[cat] || cat}</Badge>;
  };

  const canAccess = user?.role === 'admin' || user?.role === 'superadmin';

  if (!canAccess) {
    return (
      <div className="p-8 text-center">
        <AlertCircle className="h-12 w-12 mx-auto text-amber-500 mb-4" />
        <h2 className="text-xl font-semibold text-slate-700">Acceso Restringido</h2>
        <p className="text-slate-500 mt-2">Solo administradores pueden ver las finanzas.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="finance-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-[#0B3B71]" style={{ fontFamily: 'Fraunces, serif' }}>
          Finanzas
        </h1>
        <p className="text-slate-500 mt-1">Resumen de ingresos y egresos</p>
      </div>

      {/* KPIs */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-50">
                  <DollarSign className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Ingresos Hoy</p>
                  <p className="text-lg font-bold text-green-600" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {formatCurrency(stats.ingresos_hoy)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-50">
                  <TrendingUp className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Ingresos Mes</p>
                  <p className="text-lg font-bold text-blue-600" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {formatCurrency(stats.ingresos_mes)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-red-50">
                  <TrendingDown className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Egresos Mes</p>
                  <p className="text-lg font-bold text-red-600" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {formatCurrency(stats.egresos_mes)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${stats.balance_mes >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                  <PiggyBank className={`h-5 w-5 ${stats.balance_mes >= 0 ? 'text-green-600' : 'text-red-600'}`} />
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Balance Mes</p>
                  <p className={`text-lg font-bold ${stats.balance_mes >= 0 ? 'text-green-600' : 'text-red-600'}`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {formatCurrency(stats.balance_mes)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Ingresos por categoría */}
      {stats?.ingresos_por_categoria && Object.keys(stats.ingresos_por_categoria).length > 0 && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-semibold text-[#0B3B71]">
              Ingresos por Categoría (Este Mes)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {Object.entries(stats.ingresos_por_categoria).map(([cat, monto]) => (
                <div key={cat} className="p-3 bg-slate-50 rounded-lg">
                  <div className="mb-2">{getCategoryBadge(cat)}</div>
                  <p className="font-mono font-bold text-slate-800">{formatCurrency(monto)}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <Tabs value={periodo} onValueChange={setPeriodo}>
            <TabsList className="bg-slate-100">
              <TabsTrigger value="hoy">Hoy</TabsTrigger>
              <TabsTrigger value="semana">Semana</TabsTrigger>
              <TabsTrigger value="mes">Mes</TabsTrigger>
            </TabsList>
          </Tabs>
        </CardContent>
      </Card>

      {/* Incomes List */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold text-[#0B3B71] flex items-center gap-2">
            <ArrowUpRight className="h-5 w-5 text-green-600" />
            Ingresos ({incomes.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex gap-2">
                <span className="w-3 h-3 bg-[#0891B2] rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                <span className="w-3 h-3 bg-[#0891B2] rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                <span className="w-3 h-3 bg-[#0891B2] rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
              </div>
            </div>
          ) : incomes.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <DollarSign className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>No hay ingresos en este período</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left py-3 px-2 text-xs font-medium text-slate-500 uppercase">Fecha</th>
                    <th className="text-left py-3 px-2 text-xs font-medium text-slate-500 uppercase">Categoría</th>
                    <th className="text-left py-3 px-2 text-xs font-medium text-slate-500 uppercase">Descripción</th>
                    <th className="text-left py-3 px-2 text-xs font-medium text-slate-500 uppercase">Referencia</th>
                    <th className="text-right py-3 px-2 text-xs font-medium text-slate-500 uppercase">Monto</th>
                  </tr>
                </thead>
                <tbody>
                  {incomes.map((income) => (
                    <tr key={income.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                      <td className="py-3 px-2 text-sm text-slate-600">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-3 w-3 text-slate-400" />
                          {income.fecha}
                        </div>
                      </td>
                      <td className="py-3 px-2">
                        {getCategoryBadge(income.categoria)}
                      </td>
                      <td className="py-3 px-2 text-sm text-slate-800">
                        {income.descripcion}
                      </td>
                      <td className="py-3 px-2 text-sm text-slate-500 font-mono">
                        {income.referencia || '-'}
                      </td>
                      <td className="py-3 px-2 text-right">
                        <span className="font-mono font-semibold text-green-600">
                          +{formatCurrency(income.monto)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* CxP Alert */}
      {stats?.cxp_pendientes > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600" />
              <div>
                <p className="font-medium text-amber-800">Cuentas por Pagar Pendientes</p>
                <p className="text-sm text-amber-700">
                  Tienes <span className="font-mono font-bold">{formatCurrency(stats.cxp_pendientes)}</span> en facturas por pagar
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default FinancePage;
